import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { setTaskDone } from '../lib/tasks';
import ViewsNav from '../components/ViewsNav';
import AccessInfo from '../components/AccessInfo';

// Crew home screen: only the tasks and jobs assigned to them (the database returns nothing else).
// Crew can mark tasks done, add notes and photos, and send a quote on an assigned job.
const formatDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;

export default function MyWork({ session, currentView, onNavigate }) {
  const me = session.user.email.toLowerCase();
  const [tasks, setTasks] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    const [t, j, p] = await Promise.all([
      supabase.from('tasks').select('*, properties(name)').order('due_date', { ascending: true, nullsFirst: false }),
      supabase.from('jobs').select('*, properties(name)').order('created_at', { ascending: false }),
      supabase.from('properties').select('id, name, address, checkin_time, checkout_time').order('name'),
    ]);
    setTasks(t.data || []);
    setJobs(j.data || []);
    setProperties(p.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(null), 4000); };

  const markDone = async (task) => {
    const { error, nextDueDate } = await setTaskDone(task, true);
    if (error) { flash(`Couldn't mark it done: ${error.message}`); return; }
    flash(nextDueDate ? `Done. Next one is due ${formatDate(nextDueDate)}.` : 'Marked done.');
    await load();
  };

  const openTasks = tasks.filter(t => !t.done);
  const doneTasks = tasks.filter(t => t.done);

  return (
    <div className="app-layout">
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1>Property Manager</h1>
          <p>{session.user.email}</p>
        </div>
        <ViewsNav currentView={currentView} onNavigate={onNavigate} />
        <div className="sidebar-footer">
          <button className="logout-btn" onClick={() => supabase.auth.signOut()}>Sign Out</button>
        </div>
      </aside>

      <main className="main-content">
        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-value">{openTasks.length}</span>
            <span className="stat-label">Open Tasks</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{jobs.filter(j => j.status !== 'Complete').length}</span>
            <span className="stat-label">Open Jobs</span>
          </div>
        </div>

        {toast && <div className="recurring-toast">{toast}</div>}

        <div className="content-area">
          {loading ? (
            <div className="empty-state"><p>Loading your work...</p></div>
          ) : (
            <>
              <div className="content-header"><h2>My tasks</h2><p>{openTasks.length} open</p></div>
              {openTasks.length === 0 ? (
                <div className="empty-state"><h3>Nothing assigned right now</h3></div>
              ) : (
                <div className="task-list">
                  {openTasks.map(t => <WorkItem key={t.id} kind="task" item={t} me={me} onDone={markDone} />)}
                </div>
              )}

              <div className="content-header" style={{ marginTop: 24 }}><h2>My jobs</h2><p>{jobs.length} assigned</p></div>
              {jobs.length === 0 ? (
                <div className="empty-state"><h3>No jobs assigned</h3></div>
              ) : (
                <div className="task-list">
                  {jobs.map(j => <WorkItem key={j.id} kind="job" item={j} me={me} />)}
                </div>
              )}

              {properties.length > 0 && (
                <>
                  <div className="content-header" style={{ marginTop: 24 }}><h2>Property access</h2></div>
                  {properties.map(p => (
                    <div key={p.id} className="property-info-card" style={{ marginBottom: 12 }}>
                      <div className="property-info-body">
                        <strong>{p.name}</strong>
                        {p.address && <div style={{ color: '#888', fontSize: 13 }}>{p.address}</div>}
                        <AccessInfo propertyId={p.id} />
                      </div>
                    </div>
                  ))}
                </>
              )}

              {doneTasks.length > 0 && (
                <>
                  <div className="content-header" style={{ marginTop: 24 }}><h2>Done</h2><p>{doneTasks.length}</p></div>
                  <div className="task-list">
                    {doneTasks.map(t => (
                      <div key={t.id} className="task-card done">
                        <div className="task-body">
                          <div className="task-title">{t.title}</div>
                          <div className="task-meta">
                            {t.properties?.name && <span className="tag tag-property">{t.properties.name}</span>}
                            {t.due_date && <span className="tag tag-due">{formatDate(t.due_date)}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

// One assigned task or job with its notes, photos and (for jobs) quotes.
function WorkItem({ kind, item, me, onDone }) {
  const parentKey = kind === 'task' ? 'task_id' : 'job_id';
  const photoTable = kind === 'task' ? 'task_photos' : 'job_photos';
  const bucket = kind === 'task' ? 'task-photos' : 'job-photos';

  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [quote, setQuote] = useState({ total_cost: '', estimated_days: '', includes: '', notes: '' });

  const loadDetails = useCallback(async () => {
    const [n, ph, q] = await Promise.all([
      supabase.from('work_notes').select('*').eq(parentKey, item.id).order('created_at'),
      supabase.from(photoTable).select('*').eq(parentKey, item.id).order('created_at'),
      kind === 'job' ? supabase.from('proposals').select('*').eq('job_id', item.id) : Promise.resolve({ data: [] }),
    ]);
    setNotes(n.data || []);
    setQuotes(q.data || []);
    const withUrls = await Promise.all((ph.data || []).map(async p => {
      const { data } = await supabase.storage.from(bucket).createSignedUrl(p.storage_path, 600);
      return { ...p, url: data?.signedUrl };
    }));
    setPhotos(withUrls);
  }, [item.id, kind, parentKey, photoTable, bucket]);

  useEffect(() => { if (open) loadDetails(); }, [open, loadDetails]);

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true); setError('');
    const { error } = await supabase.from('work_notes').insert({ [parentKey]: item.id, author_email: me, body: note.trim() });
    setBusy(false);
    if (error) { setError(error.message); return; }
    setNote('');
    loadDetails();
  };

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    setBusy(true); setError('');
    for (const file of files) {
      const path = `${item.id}/${Date.now()}-${file.name}`;
      const { error: upErr } = await supabase.storage.from(bucket).upload(path, file);
      if (upErr) { setError(upErr.message); continue; }
      const { error: rowErr } = await supabase.from(photoTable).insert({ [parentKey]: item.id, storage_path: path, filename: file.name });
      if (rowErr) setError(rowErr.message);
    }
    setBusy(false);
    loadDetails();
  };

  const sendQuote = async () => {
    if (!quote.total_cost) { setError('Enter the quote amount.'); return; }
    setBusy(true); setError('');
    const { error } = await supabase.from('proposals').insert({
      job_id: item.id,
      vendor_name: me,
      submitted_by: me,
      total_cost: parseFloat(quote.total_cost),
      estimated_days: quote.estimated_days ? parseInt(quote.estimated_days, 10) : null,
      includes: quote.includes.trim() || null,
      notes: quote.notes.trim() || null,
      status: 'Pending',
      submitted_date: new Date().toISOString().split('T')[0],
    });
    setBusy(false);
    if (error) { setError(error.message); return; }
    setQuote({ total_cost: '', estimated_days: '', includes: '', notes: '' });
    loadDetails();
  };

  return (
    <div className="task-card" data-work-item={item.id}>
      <div className="task-body">
        <div className="task-title">{item.title}</div>
        <div className="task-meta">
          {item.properties?.name && <span className="tag tag-property">{item.properties.name}</span>}
          {kind === 'task' && item.priority && <span className={`tag tag-priority-${item.priority}`}>{item.priority}</span>}
          {kind === 'task' && item.due_date && <span className="tag tag-due">Due {formatDate(item.due_date)}</span>}
          {kind === 'task' && item.is_recurring && <span className="tag tag-recurring">🔁 {item.recurrence_interval}</span>}
          {kind === 'job' && <span className="tag tag-category">{item.status}</span>}
        </div>
        {(item.notes || item.description) && (
          <div style={{ fontSize: 13, color: '#888', marginTop: 6 }}>{item.description || item.notes}</div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {kind === 'task' && (
            <button className="btn btn-primary btn-sm" onClick={() => onDone(item)}>Mark done</button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => setOpen(!open)}>
            {open ? 'Hide details' : kind === 'job' ? 'Notes, photos & quote' : 'Notes & photos'}
          </button>
        </div>

        {open && (
          <div className="work-details" style={{ marginTop: 12 }}>
            {error && <div className="error-msg">{error}</div>}

            <div className="form-group">
              <label>Notes</label>
              {notes.length === 0 && <div style={{ color: '#888', fontSize: 13 }}>No notes yet.</div>}
              {notes.map(n => (
                <div key={n.id} style={{ fontSize: 13, marginBottom: 6 }}>
                  <strong>{n.author_email}</strong> · {new Date(n.created_at).toLocaleString()}<br />{n.body}
                </div>
              ))}
              <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Add a note..." />
              <button className="btn btn-secondary btn-sm" onClick={addNote} disabled={busy} style={{ marginTop: 6 }}>Add note</button>
            </div>

            <div className="form-group">
              <label>Photos</label>
              {photos.length > 0 && (
                <div className="photo-thumbnails">
                  {photos.map(p => (
                    <div key={p.id} className="photo-thumb"><img src={p.url} alt={p.filename || 'Photo'} /></div>
                  ))}
                </div>
              )}
              <label className="photo-upload-area">
                <input type="file" accept="image/*" multiple onChange={addPhotos} style={{ display: 'none' }} />
                <span>{busy ? 'Uploading...' : '+ Add Photos'}</span>
              </label>
            </div>

            {kind === 'job' && (
              <div className="form-group">
                <label>My quotes</label>
                {quotes.length === 0 && <div style={{ color: '#888', fontSize: 13 }}>No quote sent yet.</div>}
                {quotes.map(q => (
                  <div key={q.id} style={{ fontSize: 13, marginBottom: 6 }}>
                    ${parseFloat(q.total_cost).toLocaleString()} · {q.status}{q.estimated_days ? ` · ${q.estimated_days} days` : ''}
                  </div>
                ))}
                <div className="form-row">
                  <input type="number" min="0" placeholder="Amount ($)" value={quote.total_cost}
                    onChange={e => setQuote({ ...quote, total_cost: e.target.value })} />
                  <input type="number" min="0" placeholder="Days" value={quote.estimated_days}
                    onChange={e => setQuote({ ...quote, estimated_days: e.target.value })} />
                </div>
                <input placeholder="What's included" value={quote.includes} style={{ marginTop: 6 }}
                  onChange={e => setQuote({ ...quote, includes: e.target.value })} />
                <textarea placeholder="Notes" value={quote.notes} style={{ marginTop: 6 }}
                  onChange={e => setQuote({ ...quote, notes: e.target.value })} />
                <button className="btn btn-primary btn-sm" onClick={sendQuote} disabled={busy} style={{ marginTop: 6 }}>Send quote</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
